// @ts-strict-ignore
import type { ComputedRef, Ref, SetupContext } from 'vue'
import type { IToolingFormComposable, IToolingFormComposableProps } from '../../../composables'

import type { EqualizeMessages } from '../../../models'
import type { Equalize } from '../../../modules/equalize'
import type { EqualizeDomainService } from '../../../services/interfaces'

import { computed, getCurrentInstance } from 'vue'
import { CONTAINER } from '../../../config'
import { TYPES } from '../../../config/inversify.types'
import { EndpointContextEnum } from '../../../enums'
import {
  getBoostingPreviewParams,
  getFunctionScorePreviewParams,
  getSearchableFieldPreviewParams,
} from '../../../utils/equalize.utils'

interface IEqualizeViewDataComputedProps {
  toolingForm: IToolingFormComposable<Equalize>
  equalizeFormData: { notesShown: Ref<boolean> }
  messages: EqualizeMessages
  context?: Ref<string>
}

interface IEqualizeFormComputed {
  notesBtnText: ComputedRef<string>
  isValid: ComputedRef<boolean>
  isReadOnly: ComputedRef<boolean>
  previewParams: ComputedRef<Record<string, any>>
  semanticMatchVisible: ComputedRef<boolean>
  semanticMatchDisabled: ComputedRef<boolean>
  sponsoredScoreVisible: ComputedRef<boolean>
  sponsoredScoreDisabled: ComputedRef<boolean>
}

export const computedFn = (
  props: IToolingFormComposableProps,
  ctx: SetupContext,
  { toolingForm, equalizeFormData, messages, context }: IEqualizeViewDataComputedProps,
): IEqualizeFormComputed => {
  const {
    proxy: { $epb },
  } = getCurrentInstance()

  const { item } = toolingForm
  const { notesShown } = equalizeFormData

  const domainService = CONTAINER.get<EqualizeDomainService>(TYPES.EqualizeDomainService)

  const notesBtnText = computed(() => {
    return notesShown.value
      ? messages.fields.notes.actions.hide
      : messages.fields.notes.actions.show
  })

  const isValid = computed(() => {
    const multiMatchHasSelectedValue =
      item.value.features.multiMatchSearch.config.searchableFields.some(
        (field: any) => !!field.boost,
      )

    const currentContext = context?.value

    return (
      domainService.isValid(item.value) &&
      ((multiMatchHasSelectedValue && currentContext === EndpointContextEnum.QUERY) ||
        currentContext === EndpointContextEnum.FILTER)
    )
  })

  const isReadOnly = computed((): boolean => {
    return toolingForm.readonly.value || item.value.isDefault
  })

  const previewParams = computed(
    (): Record<string, any> => ({
      ...getSearchableFieldPreviewParams([
        ...item.value.features.multiMatchSearch.config.searchableFields,
        ...item.value.features.semanticMatchSearch.config.searchableFields,
      ]),
      ...getBoostingPreviewParams(item.value.features.boosting.config.boosts),
      ...getFunctionScorePreviewParams([
        ...item.value.features.functionScore.config.functions,
        ...item.value.features.sponsoredScore.config.functions,
      ]),
    }),
  )

  const semanticMatchVisible = computed((): boolean => {
    return (
      ($epb.showEqualizeDisabledFeatures ||
        !!item.value.features.semanticMatchSearch.config.searchableFields.length) &&
      EndpointContextEnum.QUERY === context.value
    )
  })

  const semanticMatchDisabled = computed((): boolean => {
    return !item.value.features.semanticMatchSearch.config.searchableFields.length
  })

  const sponsoredScoreVisible = computed((): boolean => {
    return (
      ($epb.showEqualizeDisabledFeatures ||
        !!item.value.features.sponsoredScore.config.functions.length) &&
      EndpointContextEnum.QUERY === context.value
    )
  })

  const sponsoredScoreDisabled = computed((): boolean => {
    return !item.value.features.sponsoredScore.config.functions.length
  })

  return {
    notesBtnText,
    isValid,
    isReadOnly,
    previewParams,
    semanticMatchVisible,
    semanticMatchDisabled,
    sponsoredScoreVisible,
    sponsoredScoreDisabled,
  }
}
